import { confirmDialog } from '../components/ui/ConfirmDialog';
import { EmptyState } from '../components/ui/EmptyState';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Search,
  Filter,
  Plus,
  LayoutGrid,
  List,
  MoreVertical,
  Archive,
  Trash2,
  ChevronDown,
  Building2,
  Home,
  MapPin,
  X,
  Loader2,
  ClipboardList,
  Users,
  Globe,
  Share2,
  Check,
  ArrowUpDown,
  ArrowDownNarrowWide,
  SearchX,
  FilterX,
  ArrowUpNarrowWide
} from 'lucide-react';
import { clsx } from 'clsx';
import { Property } from '../types/types';
import { PropertyCard } from '../components/ui/PropertyCard';
import { PropertyFiltersModal, AdvancedFilters } from '../components/ui/PropertyFiltersModal';
import { useCRM, type PropertySortBy } from '../contexts/CRMContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useVitrinaShare } from '../hooks/useVitrinaShare';
import { ShareOptionsDialog } from '../components/ui/ShareOptionsDialog';
import { FeaturedManager } from '../components/ui/FeaturedManager';
import { FeaturedPurchaseChooser } from '../components/ui/FeaturedPurchaseChooser';
import { useFeaturedManagement } from '../hooks/useFeaturedManagement';
import { AgencyCapsIndicator } from '../components/ui/AgencyCapsIndicator';
import { ActivateDraftModal } from '../components/property/ActivateDraftModal';
import { stripHtml } from '../lib/text';
import { slugifyAmenity } from '../lib/amenity-slug';
import { countTransferableFilters, buildExchangeUrl } from '../lib/property-filters-bridge';
import { shouldShowRenovarPublish } from '../lib/publish-renewal';
import { needsShareRenewal } from '../lib/share-renewal';
import { useSessionState } from '../hooks/useSessionState';
import { canViewAllProperties } from '../utils/permissions';
import { useLastVisitedRestore } from '../hooks/useLastVisitedRestore';
import { CurrencyTogglePill } from '../components/ui/CurrencyTogglePill';
import { onFilterRequest, takePendingFilterRequest, clearPendingFilterRequest, setScreenContext, clearScreenContext } from '../services/aiUiBridge';

interface PropiedadesViewProps {
  theme: 'light' | 'dark' | 'high-contrast';
  properties: Property[];
  onAddProperty: () => void;
  onPropertySelect: (property: Property) => void;
  onStatusChange: (id: string, newStatus: string /*Property['status']*/) => void;
  onPublishToggle: (id: string, e?: React.MouseEvent) => void;
  onShareToggle: (id: string, e?: React.MouseEvent) => void;
  onRestore?: (id: string, e?: React.MouseEvent) => void;
  onPermanentDelete?: (id: string, e?: React.MouseEvent) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

type TabType = 'activas' | 'expiradas' | 'borradores' | 'archivadas' | 'eliminadas';

// Zero-state copy per tab when nothing is filtered: why it's empty.
const EMPTY_TAB_COPY: Record<TabType, { title: string; description: string }> = {
  activas: { title: 'Aún no tienes propiedades activas', description: 'Agrega tu primera propiedad para empezar a publicarla y compartirla.' },
  expiradas: { title: 'Nada por renovar', description: 'Aquí aparecen las publicaciones vencidas o que vencen en los próximos 7 días.' },
  borradores: { title: 'No tienes borradores', description: 'Las propiedades que guardes sin terminar aparecerán aquí.' },
  archivadas: { title: 'No hay propiedades archivadas', description: 'Las propiedades que archives quedan aquí y puedes restaurarlas cuando quieras.' },
  eliminadas: { title: 'No hay propiedades eliminadas', description: 'Aquí aparecen las propiedades eliminadas permanentemente.' },
};

// Quick-access status chips rendered above the grid (desktop) and as a
// fixed bottom bar (mobile). Each chip resets a small set of advancedFilters
// fields so chip selection is single-select even though the underlying
// state is multi-dimensional.
type QuickFilterKey = 'todas' | 'publicadas' | 'compartidas' | 'destacadas' | 'expiradas' | 'compartir_vence';
const QUICK_FILTERS: Array<{ key: QuickFilterKey; label: string }> = [
  { key: 'todas',       label: 'Todas' },
  { key: 'publicadas',  label: 'Publicadas' },
  { key: 'compartidas', label: 'Compartidas' },
  { key: 'destacadas',  label: 'Destacadas' },
  { key: 'expiradas',   label: 'Expirando' },
  // Share side of "Expirando": already expired in the network, or lapsing
  // within SHARE_RENEWAL_WINDOW_DAYS. Filtered server-side (the loaded page is
  // 14 rows, so a client-only pass would silently return almost nothing).
  { key: 'compartir_vence', label: 'Compartir por vencer' },
];

// Mirror of PropertyFiltersModal's handleReset payload so the inline
// "Limpiar" pill produces the same blank state as the modal's reset.
const DEFAULT_ADVANCED_FILTERS: AdvancedFilters = {
  operation: 'All',
  propertyStatus: 'All',
  propertyType: [],
  minPrice: '',
  maxPrice: '',
  bedrooms: [],
  minBaths: 0,
  minParking: 0,
  publishStatus: 'All',
  shareStatus: 'All',
  buildUpSizeMin: '',
  buildUpSizeMax: '',
  landSizeMin: '',
  landSizeMax: '',
  amenities: [],
  department_id: undefined,
  city_ids: [],
  sector_ids: [],
  agent_ids: [],
  proyecto_ids: [],
  is_featured: undefined,
};

export const PropiedadesView: React.FC<PropiedadesViewProps> = ({
  theme,
  properties,
  onAddProperty,
  onPropertySelect,
  onStatusChange,
  onPublishToggle,
  onShareToggle,
  onRestore,
  onPermanentDelete,
  hasMore,
  isLoadingMore,
  onLoadMore
}) => {
  const isLight = theme !== 'dark';

  // State — filter / search / tab / view mode are session-backed so a back
  // navigation from a property detail returns the user to where they were.
  const [activeTab, setActiveTab] = useSessionState<TabType>('propiedades:activeTab', 'activas');
  const [searchQuery, setSearchQuery] = useSessionState<string>('propiedades:searchQuery', '');
  // Debounced version of searchQuery — pushed into the server filter so the
  // search box finds properties past page 1 (especially for super_admin's
  // cross-agency view where the wanted record may sit at row #1500+).
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);
  // null = untouched this tab → falls back to the saved per-user default
  // (Ajustes de usuario → default_properties_view). Resolved below, after
  // useAuth provides realProfile.
  const [viewModeSession, setViewMode] = useSessionState<'grid' | 'list' | null>('propiedades:viewMode', null);
  // Sort preference — like viewMode, it's a session-backed preference (not a
  // filter), so it survives the unmount cleanup that clears search/advFilters.
  const [sortBy, setSortBy] = useSessionState<PropertySortBy>('propiedades:sortBy', 'default');
  // Sort direction — 'desc' (newest/most-recent first) is the historical
  // default; the arrow toggle flips to 'asc' (oldest first).
  const [sortDir, setSortDir] = useSessionState<'asc' | 'desc'>('propiedades:sortDir', 'desc');
  // Portal target for the app top-bar action slot (#app-header-slot in
  // AppLayout). Resolved after mount so the first render — before the parent's
  // DOM is committed — doesn't try to portal into a null node. On <md the slot
  // is display:none, so the injected toolbar is invisible there (mobile keeps
  // its FAB + bottom search); we still only render it into the slot when found.
  const [searchParams, setSearchParams] = useSearchParams();
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setHeaderSlot(document.getElementById('app-header-slot')); }, []);
  // const [propertyTypeFilter, setPropertyTypeFilter] = useState<string>('All');
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [isPropertyViewLoading, setIsPropertyViewLoading] = useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [rawAdvancedFilters, setAdvancedFilters] = useSessionState<AdvancedFilters>('propiedades:advFilters', DEFAULT_ADVANCED_FILTERS);
  // Merge with the full default so a PARTIAL or STALE persisted shape always
  // exposes every field as its default. The old inline initial only set 4
  // fields (operation/propertyType/publishStatus/shareStatus), and a value
  // persisted before bedrooms/amenities/etc. existed lacks them too — so reads
  // like advancedFilters.bedrooms.length / propertyType.length hit undefined and
  // white-screened /propiedades (GlitchTip intver2-web #568/#569).
  const advancedFilters = useMemo<AdvancedFilters>(
    () => ({ ...DEFAULT_ADVANCED_FILTERS, ...rawAdvancedFilters }),
    [rawAdvancedFilters],
  );
  const [isFabExpanded, setIsFabExpanded] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  // Name → ID maps for property types and amenities. Properties stores them
  // as names, Intercambio as IDs, so we resolve once at load time and reuse
  // the maps when the user clicks "Buscar en Intercambio".
  const navigate = useNavigate();
  const [propertyTypeNameToId, setPropertyTypeNameToId] = useState<Map<string, number>>(new Map());
  const [amenityNameToId, setAmenityNameToId] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    Promise.all([
      supabase.from('property_types').select('id, name').eq('is_active', true),
      supabase.from('amenities').select('id, name').eq('is_active', true),
    ]).then(([ptRes, amRes]) => {
      if (cancelled) return;
      if (ptRes.data) {
        setPropertyTypeNameToId(new Map(ptRes.data.map((r: any) => [r.name as string, r.id as number])));
      }
      if (amRes.data) {
        setAmenityNameToId(new Map(amRes.data.map((r: any) => [r.name as string, r.id as number])));
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Reset search + advanced filters when the user leaves this page,
  // *unless* they're just opening / editing / creating a property
  // (anything under /propiedades/X). That preserves the "click card →
  // detail → back" loop while preventing a stuck filter from silently
  // hiding rows next time the user returns from Kanban, Dashboard,
  // Contactos, etc. View mode (grid/list) and active tab (Activas /
  // Borradores / Archivadas) stay — they're sub-views / preferences,
  // not filters.
  useEffect(() => {
    return () => {
      const goingToPropertyRoute = /^\/propiedades\/[^/]+/.test(window.location.pathname);
      if (goingToPropertyRoute) return;
      sessionStorage.removeItem('propiedades:searchQuery');
      sessionStorage.removeItem('propiedades:advFilters');
      sessionStorage.removeItem('propiedades:lastVisited');
    };
  }, []);

  const transferableCount = countTransferableFilters(advancedFilters);
  const handleSearchInExchange = useCallback(() => {
    const url = buildExchangeUrl(advancedFilters, propertyTypeNameToId, amenityNameToId);
    navigate(url);
  }, [advancedFilters, propertyTypeNameToId, amenityNameToId, navigate]);

  // Bring the user back to the card they last opened (and visually flag
  // it). Note this only works when the clicked card sits in the first
  // server page — if it was past it, "Cargar Más" wasn't replayed and the
  // hook won't find a match. Acceptable tradeoff for now.
  const { highlightedId, saveLastVisited } = useLastVisitedRestore(
    'propiedades:lastVisited',
    properties.length > 0,
  );

  // AI assistant → list bridge: apply filters emitted by the apply_filters
  // tool. On mount, consume a pending request (navigate-then-filter race);
  // while mounted, apply live requests and clear the pending slot.
  useEffect(() => {
    const pending = takePendingFilterRequest('properties');
    if (pending) {
      if (pending.filters) setAdvancedFilters(prev => ({ ...prev, ...pending.filters! }));
      if (typeof pending.searchQuery === 'string') setSearchQuery(pending.searchQuery);
    }
    const off = onFilterRequest(req => {
      if (req.view !== 'properties') return;
      if (req.filters) setAdvancedFilters(prev => ({ ...prev, ...req.filters! }));
      if (typeof req.searchQuery === 'string') setSearchQuery(req.searchQuery);
      clearPendingFilterRequest();
    });
    return off;
  }, [setAdvancedFilters, setSearchQuery]);

  // Matching counts for shared properties — re-enabled with the cache.
  //
  // The 2026-05-05 incident was caused by firing one find_matching_requirements
  // RPC PER visible card (14 concurrent ~3s queries saturating the 10-conn pool).
  // This now reads all counts for the page's shared cards in ONE query against
  // v_properties_with_match_count (an indexed cache COUNT). The wildcard-garbage
  // problem is also gone: matching is gated on op+dept+type, so counts are real.
  const [matchingCounts, setMatchingCounts] = useState<Record<string, number>>({});

  // Stable key so this only refetches when the set of shared property ids changes.
  const sharedIdsKey = useMemo(
    () => properties.filter(p => p.share_status === 'shared').map(p => p.id).sort().join(','),
    [properties]
  );
  useEffect(() => {
    if (!supabase || !sharedIdsKey) { setMatchingCounts({}); return; }
    const ids = sharedIdsKey.split(',');
    let cancelled = false;
    supabase.from('v_properties_with_match_count').select('id, match_count').in('id', ids)
      .then(({ data }) => {
        if (cancelled || !data) return;
        const m: Record<string, number> = {};
        data.forEach((r: any) => { if (r.match_count > 0) m[r.id] = r.match_count; });
        setMatchingCounts(m);
      });
    return () => { cancelled = true; };
  }, [sharedIdsKey]);
  const [matchingReqPropertyId, setMatchingReqPropertyId] = useState<string | null>(null);
  const [matchingRequirements, setMatchingRequirements] = useState<any[]>([]);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  // Activate-draft modal target. Holds the row the user clicked "Activar" on so
  // the modal can render its validation + publish/share checkboxes against it.
  const [activateTarget, setActivateTarget] = useState<Property | null>(null);

  // Fetch matching requirements detail when modal opens
  useEffect(() => {
    if (!matchingReqPropertyId || !supabase) return;
    let cancelled = false;
    setIsLoadingMatches(true);
    supabase.rpc('find_matching_requirements', { p_property_id: matchingReqPropertyId })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (data && !error) setMatchingRequirements(data);
        else if (error) console.error('Error fetching matching requirements:', error);
        setIsLoadingMatches(false);
      });
    return () => { cancelled = true; };
  }, [matchingReqPropertyId]);

  const closeMatchingModal = useCallback(() => {
    setMatchingReqPropertyId(null);
    setMatchingRequirements([]);
  }, []);

  // CRMContext hooks — pulled up so handlers below can reference setProperties.
  const { fetchProperties, isLoadingMoreProperties, isPropertiesInitialized, propertiesTotalCount, setProperties } = useCRM();
  const { isSuperAdmin, profile, realProfile } = useAuth();
  const tabs = useMemo<TabType[]>(
    () => isSuperAdmin
      ? ['activas', 'expiradas', 'borradores', 'archivadas', 'eliminadas']
      : ['activas', 'expiradas', 'borradores', 'archivadas'],
    [isSuperAdmin]
  );
  // Snap back to 'activas' if a stale sessionStorage value points at a tab
  // that's no longer available (e.g. super_admin → impersonating an agent).
  useEffect(() => {
    if (!tabs.includes(activeTab)) setActiveTab('activas');
  }, [tabs, activeTab, setActiveTab]);

  // ── Per-agent visibility scope ──────────────────────────────────────────
  // Agents default to seeing ONLY their own (assigned) properties. The
  // "Ver todas las propiedades" permission unlocks a toggle to view the whole
  // agency; agents without it are locked to their own and never see the toggle.
  // Admins / super_admins always see everything (no scoping, no toggle).
  // NOTE: this is UI/query scoping (mirrors how the permission already gates
  // private notes & documents). RLS stays agency-wide — harden at the DB layer
  // separately if a true security boundary is ever required.
  const isAgentRole = profile?.role === 'agent';
  const canToggleScope = isAgentRole && canViewAllProperties(profile); // agent WITH the permission
  // Session-backed so the choice survives a detail→back round trip. null =
  // "untouched this tab" → falls back to the saved per-user default
  // (Configuración → Ajustes de usuario → default_properties_scope, absent →
  // "Mías"). A manual flip writes a boolean that wins for the rest of the
  // tab, so a one-off switch doesn't fight the saved default — and changing
  // the default in Configuración applies immediately to any tab that hasn't
  // been flipped. realProfile (not profile): the pref belongs to the
  // logged-in user, same as everywhere else ui_preferences is read.
  const [scopeAllSession, setScopeAll] = useSessionState<boolean | null>('propiedades:scopeAll', null);
  const scopeAll = scopeAllSession ?? (realProfile?.ui_preferences?.default_properties_scope === 'todas');
  // Same null-fallback pattern for the grid/list view (declared above, before
  // realProfile was in scope).
  const viewMode: 'grid' | 'list' = viewModeSession ?? (realProfile?.ui_preferences?.default_properties_view === 'list' ? 'list' : 'grid');
  const ownAgentId = profile?.id;
  // Limit the list + counts to the current user's own properties unless they're
  // a permission-holder who has explicitly switched to "Toda la agencia".
  const restrictToOwn = isAgentRole && !(canToggleScope && scopeAll) && !!ownAgentId;

  // Push current filter state to the AI assistant's screen context.
  useEffect(() => {
    setScreenContext({
      page: 'properties',
      activeFilters: {
        tab: activeTab,
        query: debouncedSearch || undefined,
        operation: advancedFilters.operation !== 'All' ? advancedFilters.operation : undefined,
        propertyType: advancedFilters.propertyType.length > 0 ? advancedFilters.propertyType.join(', ') : undefined,
        minPrice: advancedFilters.minPrice ? Number(advancedFilters.minPrice) : undefined,
        maxPrice: advancedFilters.maxPrice ? Number(advancedFilters.maxPrice) : undefined,
        bedroomsMin: advancedFilters.bedrooms.length > 0 ? Math.min(...advancedFilters.bedrooms.map(Number)) : undefined,
        publishStatus: advancedFilters.publishStatus !== 'All' ? advancedFilters.publishStatus : undefined,
        shareStatus: advancedFilters.shareStatus !== 'All' ? advancedFilters.shareStatus : undefined,
        featured: advancedFilters.is_featured || undefined,
        scopeAll: scopeAll || undefined,
      },
    });
    return () => clearScreenContext();
  }, [activeTab, debouncedSearch, advancedFilters, scopeAll]);

  // Featured-property management — owns summary + activate/deactivate so the
  // existing properties grid can be the picker (no modal, no second list).
  const featuredMgmt = useFeaturedManagement();
  const [featuredManageMode, setFeaturedManageMode] = useState(false);
  // The Destacar / Quitar / Cambiar buttons only exist on grid cards, so
  // "Gestionar" shows the grid even when the saved view is the list (a client
  // whose browser remembered "list" saw Gestionar with no buttons at all).
  const showGrid = viewMode === 'grid' || featuredManageMode;
  const slotsAvailable = (featuredMgmt.totals?.total_available ?? 0) > 0;
  // Optimistic flip: activate/deactivate only refresh the featured *summary*,
  // not the properties array, so without patching `is_featured` locally the
  // card icon stays stale until the next fetchProperties. We confirm BEFORE
  // the optimistic flip so a cancelled deactivate doesn't briefly toggle the
  // icon.
  // When more than one purchase can pay, the agent picks; this holds which
  // property is waiting on that answer and why.
  const [purchasePrompt, setPurchasePrompt] = useState<{ mode: 'activate' | 'switch'; propertyId: string } | null>(null);

  // Shared tail of an activation, whichever purchase paid for it.
  const finishActivate = useCallback(async (propertyId: string, purchaseId?: string): Promise<boolean> => {
    setProperties(prev => prev.map(p =>
      String(p.id) === propertyId ? { ...p, is_featured: true } : p
    ));
    const ok = await featuredMgmt.activate(propertyId, purchaseId);
    if (!ok) {
      setProperties(prev => prev.map(p =>
        String(p.id) === propertyId ? { ...p, is_featured: false } : p
      ));
    }
    return ok;
  }, [featuredMgmt, setProperties]);

  const handleToggleFeatured = useCallback(async (propertyId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const prop = properties.find(p => String(p.id) === propertyId);
    if (!prop) return;
    if (prop.is_featured) {
      // eslint-disable-next-line no-alert
      if (!await confirmDialog('¿Quitar el destaque de esta propiedad? El slot quedará libre para activar en otra.')) return;
      setProperties(prev => prev.map(p =>
        String(p.id) === propertyId ? { ...p, is_featured: false } : p
      ));
      const ok = await featuredMgmt.deactivate(propertyId);
      if (!ok) {
        setProperties(prev => prev.map(p =>
          String(p.id) === propertyId ? { ...p, is_featured: true } : p
        ));
      }
      return;
    }
    // Two or more purchases could pay — ask instead of spending one silently.
    if (featuredMgmt.needsPurchaseChoice) {
      setPurchasePrompt({ mode: 'activate', propertyId });
      return;
    }
    await finishActivate(propertyId);
  }, [properties, featuredMgmt, setProperties, finishActivate]);

  // "Cambiar" on an already-destacada card — always a choice, never implicit.
  const handleSwitchFeatured = useCallback((propertyId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPurchasePrompt({ mode: 'switch', propertyId });
  }, []);

  // Feature a property chosen from the FeaturedManager name typeahead. The
  // picked property may not be in the loaded grid (search spans the whole
  // agency server-side), so patch is_featured only if it's currently loaded.
  const handlePickFeatured = useCallback(async (propertyId: string): Promise<boolean> => {
    if (featuredMgmt.needsPurchaseChoice) {
      setPurchasePrompt({ mode: 'activate', propertyId });
      return true;   // the picker closes; the chooser takes over from here
    }
    return finishActivate(propertyId);
  }, [featuredMgmt, finishActivate]);

  // Force grid mode on mobile
  React.useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth < 768 && viewMode === 'list') {
        setViewMode('grid');
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [viewMode]);

  // Push tab + every filter that maps to a DB column into the server query so
  // pagination respects them. Without this, an agency with e.g. 2 published
  // properties at row #15+ shows 0 matches when "Publicadas" is selected
  // because the client filter only sees the first 14 rows fetched. On unmount
  // we reset to no filters so other consumers (Dashboard, Kanban,
  // AppointmentModal) read the unfiltered set from CRMContext.
  // Mirror fetchProperties into a ref so the unmount cleanup can call the
  // latest version without listing it as an effect dependency. Without this
  // the cleanup re-fires every time `fetchProperties` identity changes
  // (e.g. profile reload, impersonation toggle), which retriggers a fetch
  // mid-session and contributes to the parallel-request stampede.
  const fetchPropertiesRef = useRef(fetchProperties);
  useEffect(() => { fetchPropertiesRef.current = fetchProperties; }, [fetchProperties]);
  // Searching = the user typed something AND either the debounce hasn't
  // settled OR the server query is in flight. Used by the search input
  // (spinner) and the empty state (suppress "no results" until done).
  const isSearching =
    searchQuery.trim().length > 0 &&
    (searchQuery.trim() !== debouncedSearch || isLoadingMoreProperties);
  useEffect(() => {
    fetchProperties(1, false, {
      activeTab,
      sortBy,
      sortDir,
      publishStatus:
        advancedFilters.publishStatus && advancedFilters.publishStatus !== 'All'
          ? advancedFilters.publishStatus
          : undefined,
      shareStatus:
        advancedFilters.shareStatus && advancedFilters.shareStatus !== 'All'
          ? advancedFilters.shareStatus
          : undefined,
      operation:
        advancedFilters.operation && advancedFilters.operation !== 'All'
          ? advancedFilters.operation
          : undefined,
      propertyStatus:
        advancedFilters.propertyStatus && advancedFilters.propertyStatus !== 'All'
          ? advancedFilters.propertyStatus
          : undefined,
      department_id: advancedFilters.department_id,
      city_ids: advancedFilters.city_ids && advancedFilters.city_ids.length > 0 ? advancedFilters.city_ids : undefined,
      sector_ids: advancedFilters.sector_ids && advancedFilters.sector_ids.length > 0 ? advancedFilters.sector_ids : undefined,
      // Per-agent scope wins over the modal's agent picker: when restricted to
      // own, force the current user's id regardless of any agent_ids selection.
      agent_ids: restrictToOwn && ownAgentId
        ? [ownAgentId]
        : (advancedFilters.agent_ids && advancedFilters.agent_ids.length > 0 ? advancedFilters.agent_ids : undefined),
      proyecto_ids: advancedFilters.proyecto_ids && advancedFilters.proyecto_ids.length > 0 ? advancedFilters.proyecto_ids : undefined,
      // Resolve selected type names → ids via the loaded property_types map.
      // If the map hasn't loaded yet (initial render), pass undefined so the
      // query isn't artificially narrowed; the effect will re-run once the
      // map fills in. Filters past the loaded page would otherwise be
      // dropped by client-only filtering — see bug 2026-05-12 (Olga / Bodega).
      property_type_ids: (() => {
        const names = advancedFilters.propertyType || [];
        if (names.length === 0 || propertyTypeNameToId.size === 0) return undefined;
        const ids = names.map(n => propertyTypeNameToId.get(n)).filter((id): id is number => typeof id === 'number');
        return ids.length > 0 ? ids : undefined;
      })(),
      // Numeric filters — pushed to the server for the same pagination
      // reason. The modal stores these as strings (raw input values), so
      // coerce to number and treat 0 / NaN as "no filter".
      min_price: Number(advancedFilters.minPrice) > 0 ? Number(advancedFilters.minPrice) : undefined,
      max_price: Number(advancedFilters.maxPrice) > 0 ? Number(advancedFilters.maxPrice) : undefined,
      bedrooms: advancedFilters.bedrooms && advancedFilters.bedrooms.length > 0 ? advancedFilters.bedrooms : undefined,
      min_baths: typeof advancedFilters.minBaths === 'number' && advancedFilters.minBaths > 0 ? advancedFilters.minBaths : undefined,
      min_parking: typeof advancedFilters.minParking === 'number' && advancedFilters.minParking > 0 ? advancedFilters.minParking : undefined,
      min_built_up_area: Number(advancedFilters.buildUpSizeMin) > 0 ? Number(advancedFilters.buildUpSizeMin) : undefined,
      max_built_up_area: Number(advancedFilters.buildUpSizeMax) > 0 ? Number(advancedFilters.buildUpSizeMax) : undefined,
      min_lot_size_m2: Number(advancedFilters.landSizeMin) > 0 ? Number(advancedFilters.landSizeMin) : undefined,
      max_lot_size_m2: Number(advancedFilters.landSizeMax) > 0 ? Number(advancedFilters.landSizeMax) : undefined,
      amenities: advancedFilters.amenities && advancedFilters.amenities.length > 0 ? advancedFilters.amenities : undefined,
      is_featured: advancedFilters.is_featured ? true : undefined,
      search: debouncedSearch || undefined,
    });
  }, [
    activeTab,
    sortBy,
    sortDir,
    advancedFilters.publishStatus,
    advancedFilters.shareStatus,
    advancedFilters.is_featured,
    advancedFilters.operation,
    advancedFilters.propertyStatus,
    advancedFilters.department_id,
    (advancedFilters.city_ids || []).join(','),
    (advancedFilters.sector_ids || []).join(','),
    (advancedFilters.agent_ids || []).join(','),
    (advancedFilters.proyecto_ids || []).join(','),
    restrictToOwn,
    ownAgentId,
    (advancedFilters.propertyType || []).join(','),
    advancedFilters.minPrice,
    advancedFilters.maxPrice,
    (advancedFilters.bedrooms || []).join(','),
    advancedFilters.minBaths,
    advancedFilters.minParking,
    advancedFilters.buildUpSizeMin,
    advancedFilters.buildUpSizeMax,
    advancedFilters.landSizeMin,
    advancedFilters.landSizeMax,
    (advancedFilters.amenities || []).join(','),
    propertyTypeNameToId,
    debouncedSearch,
    fetchProperties,
  ]);
  useEffect(() => {
    return () => {
      // Only fires on actual unmount (empty deps + ref-captured callback).
      // Resets the ref filters so other consumers (Dashboard, Kanban,
      // AppointmentModal) read the unfiltered set from CRMContext.
      fetchPropertiesRef.current(1, false, {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter properties based on tab, search, and type
  const filteredProperties = useMemo(() => {
    return properties.filter(p => {
      const isArchived = (p as any).is_deleted === true || (p as any).crm_status === 'archived' || p.property_status === 'archived' || p.status === 'Archivada' || p.status === 'Deleted';
      // Featured-manage mode narrows the grid to only candidates: currently
      // featured (so user can quickly un-feature) + published-and-not-archived
      // (only those can take a featured slot — unpublished are blocked).
      if (featuredManageMode) {
        if (isArchived) return false;
        if (!p.is_featured && p.publish_status !== 'published') return false;
      }
      // 1. Tab Filter
      if (activeTab === 'activas') {
        if (isArchived) return false;
        // Keep expired out of Activas unless the user is explicitly filtering
        // for the Expiradas set (chip / advanced modal).
        if (advancedFilters.publishStatus !== 'expired' && p.publish_status === 'expired') return false;
        if ((p as any).crm_status === 'draft') return false;
      } else if (activeTab === 'expiradas') {
        // "Expiradas" = needs renewal: expired OR published & within 7 days.
        if (!shouldShowRenovarPublish(p)) return false;
      } else if (activeTab === 'borradores') {
        if ((p as any).crm_status !== 'draft') return false;
      } else if (activeTab === 'archivadas') {
        if (!isArchived) return false;
        if (p.permanently_deleted_at) return false;
      } else if (activeTab === 'eliminadas') {
        if (!p.permanently_deleted_at) return false;
      }

      // 2. Search — handled server-side (CRMContext.fetchProperties: ii_id/title/
      // zona AND owner name, across all pages). No client re-filter: owner name
      // isn't on the list row (PII, not joined), so re-matching here silently
      // dropped owner-matched results. ponytail: server search is the source of truth.

      // 3. Quick Filters (Property Type - handled by advanced filters now)
      // if (propertyTypeFilter !== 'All' && p.type !== propertyTypeFilter) return false;

      // 4. Advanced Filters
      if (advancedFilters.operation && advancedFilters.operation !== 'All') {
        if (p.operation !== advancedFilters.operation) return false;
      }
      if (advancedFilters.propertyType && advancedFilters.propertyType.length > 0) {
        if (!advancedFilters.propertyType.includes(p.property_type_name || p.property_type || '')) return false;
      }
      if (advancedFilters.minPrice && p.price < Number(advancedFilters.minPrice)) return false;
      if (advancedFilters.maxPrice && p.price > Number(advancedFilters.maxPrice)) return false;
      if (advancedFilters.bedrooms && advancedFilters.bedrooms.length > 0) {
        if (!advancedFilters.bedrooms.some(beds => beds === 5 ? (p.bedrooms || 0) >= 5 : (p.bedrooms || 0) === beds)) return false;
      }
      if (advancedFilters.buildUpSizeMin && (p.built_up_area || 0) < Number(advancedFilters.buildUpSizeMin)) return false;
      if (advancedFilters.buildUpSizeMax && (p.built_up_area || 0) > Number(advancedFilters.buildUpSizeMax)) return false;
      if (advancedFilters.landSizeMin && (!(p.lot_size_varas || 0) || (p.lot_size_varas || 0) < Number(advancedFilters.landSizeMin))) return false;
      if (advancedFilters.landSizeMax && (!(p.lot_size_varas || 0) || (p.lot_size_varas || 0) > Number(advancedFilters.landSizeMax))) return false;
      if (advancedFilters.amenities && advancedFilters.amenities.length > 0) {
        // CRMContext list rows carry the JSONB `services` column directly;
        // `amenities` is a Vitrina/detail-page hydration shape that the list
        // doesn't populate. `services` mixes canonical names ("Balcón", new
        // CRM saves) with legacy slug keys ("balcon", migrated rows); match
        // either form so this belt-and-suspenders client filter doesn't
        // re-drop the same legacy rows the server filter now lets through.
        const present: string[] = ((p as any).services || (p as any).amenities || []) as string[];
        const presentSet = new Set(present.map(s => String(s)));
        const allPresent = advancedFilters.amenities.every(a =>
          presentSet.has(a) || presentSet.has(slugifyAmenity(a))
        );
        if (!allPresent) return false;
      }
      if (advancedFilters.minBaths && (p.bathrooms || 0) < advancedFilters.minBaths) return false;
      if (advancedFilters.minParking && (!(p.parking || 0) || (p.parking || 0) < advancedFilters.minParking)) return false;
      if (advancedFilters.publishStatus && advancedFilters.publishStatus !== 'All') {
        if (advancedFilters.publishStatus === 'expired') {
          // "Expirada" filter = needs renewal: expired OR published & within 7 days.
          if (!shouldShowRenovarPublish(p)) return false;
        } else if (p.publish_status !== advancedFilters.publishStatus) {
          return false;
        }
      }
      if (advancedFilters.shareStatus && advancedFilters.shareStatus !== 'All') {
        if (advancedFilters.shareStatus === 'expiring') {
          // Mirrors the server filter (CRMContext): expired in the network, or
          // lapsing within the review window.
          if (!needsShareRenewal(p)) return false;
        } else if (p.share_status !== advancedFilters.shareStatus) {
          return false;
        }
      }
      if (advancedFilters.is_featured && !p.is_featured) return false;
      if (advancedFilters.department_id && (p as any).department_id !== advancedFilters.department_id) return false;
      if (advancedFilters.city_ids && advancedFilters.city_ids.length > 0 && !advancedFilters.city_ids.includes((p as any).city_id)) return false;
      if (advancedFilters.sector_ids && advancedFilters.sector_ids.length > 0 && !advancedFilters.sector_ids.includes((p as any).sector_id)) return false;
      if (advancedFilters.agent_ids && advancedFilters.agent_ids.length > 0 && !advancedFilters.agent_ids.includes((p as any).agent_id)) return false;
      if (advancedFilters.proyecto_ids && advancedFilters.proyecto_ids.length > 0 && !advancedFilters.proyecto_ids.includes((p as any).proyecto_id)) return false;
      // Per-agent scope guard (belt-and-suspenders alongside the server filter).
      if (restrictToOwn && ownAgentId && (p as any).agent_id !== ownAgentId) return false;

      return true;
    });
  }, [properties, activeTab, searchQuery, advancedFilters, featuredManageMode, restrictToOwn, ownAgentId]);

  const handlePropertySelectToggle = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProperties(prev =>
      prev.includes(id) ? prev.filter(pId => pId !== id) : [...prev, id]
    );
  };

  // Bulk Actions — single Compartir trigger opens ShareOptionsDialog
  // (contact toggle + 4 channels), matching the property detail screen.
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (selectedProperties.length === 0) setLinkCopied(false);
  }, [selectedProperties.length]);

  // Quick-filter chip handlers. activeQuickFilter is derived from
  // advancedFilters so the chip lights up correctly even when the same
  // filter was set via the advanced modal. handleQuickFilter is single-
  // select: it overwrites the relevant fields together so selecting one
  // chip implicitly clears the others.
  const activeQuickFilter: QuickFilterKey =
    advancedFilters.is_featured ? 'destacadas' :
    advancedFilters.publishStatus === 'expired' ? 'expiradas' :
    advancedFilters.publishStatus === 'published' ? 'publicadas' :
    advancedFilters.shareStatus === 'expiring' ? 'compartir_vence' :
    advancedFilters.shareStatus === 'shared' ? 'compartidas' :
    'todas';

  const handleQuickFilter = (key: QuickFilterKey) => {
    setAdvancedFilters(prev => ({
      ...prev,
      publishStatus: key === 'publicadas' ? 'published'
                   : key === 'expiradas' ? 'expired'
                   : 'All',
      shareStatus:   key === 'compartidas' ? 'shared'
                   : key === 'compartir_vence' ? 'expiring'
                   : 'All',
      is_featured:   key === 'destacadas' ? true : undefined,
    }));
  };

  // Deep link from the dashboard pendiente: /propiedades?filtro=compartir-vence
  // preselects the share-renewal chip. Read once on mount, then stripped from
  // the URL so a later "Limpiar" isn't undone by a re-render.
  useEffect(() => {
    if (searchParams.get('filtro') !== 'compartir-vence') return;
    handleQuickFilter('compartir_vence');
    const next = new URLSearchParams(searchParams);
    next.delete('filtro');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Agency-wide counts for the quick-filter chip badges. The list is paginated,
  // so counts must come from the server (get_property_filter_counts) rather than
  // the loaded page. countSignature re-fetches when a publish/share/feature
  // change on a loaded card flips a status, so the badges stay live after toggles.
  const [filterCounts, setFilterCounts] = useState<Record<string, number> | null>(null);
  const countSignature = useMemo(
    () => properties.reduce(
      (acc, p) =>
        acc +
        (p.publish_status === 'published' ? 1 : 0) +
        (p.publish_status === 'expired' ? 2 : 0) +
        (p.share_status === 'shared' ? 4 : 0) +
        (p.is_featured ? 8 : 0),
      properties.length * 31,
    ),
    [properties],
  );
  useEffect(() => {
    if (!supabase || !profile?.agency_id) return;
    let cancelled = false;
    supabase
      // Scope the chip counts to match the visible list: own-only for restricted
      // agents, agency-wide otherwise.
      .rpc('get_property_filter_counts', {
        p_agency_id: profile.agency_id,
        p_agent_id: restrictToOwn ? ownAgentId : null,
      })
      .then(({ data, error }) => {
        if (cancelled || error || !data || (data as any).error) return;
        setFilterCounts(data as Record<string, number>);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.agency_id, countSignature, restrictToOwn, ownAgentId]);

  // QuickFilterKey → RPC count key. Chip 'expiradas' maps to 'expirando'.
  const chipCount = (key: QuickFilterKey): number | null => {
    if (!filterCounts) return null;
    const rpcKey = key === 'expiradas' ? 'expirando' : key;
    const v = filterCounts[rpcKey];
    return typeof v === 'number' ? v : null;
  };

  // True when the user has anything narrowing the grid beyond first-load
  // state — search box, any advanced filter, or a non-"Todas" quick chip.
  // Drives the inline "Limpiar" pill so it disappears when there's nothing
  // to clear (keeps the toolbar quiet by default).
  const hasActiveFilters = useMemo(() => {
    if (searchQuery.trim()) return true;
    const a = advancedFilters;
    if (a.operation && a.operation !== 'All') return true;
    if (a.propertyStatus && a.propertyStatus !== 'All') return true;
    if (a.publishStatus && a.publishStatus !== 'All') return true;
    if (a.shareStatus && a.shareStatus !== 'All') return true;
    if (a.propertyType && a.propertyType.length > 0) return true;
    if (a.bedrooms && a.bedrooms.length > 0) return true;
    if (a.amenities && a.amenities.length > 0) return true;
    if (a.city_ids && a.city_ids.length > 0) return true;
    if (a.sector_ids && a.sector_ids.length > 0) return true;
    if (a.agent_ids && a.agent_ids.length > 0) return true;
    if (a.proyecto_ids && a.proyecto_ids.length > 0) return true;
    if (a.department_id) return true;
    if (a.minPrice || a.maxPrice) return true;
    if (a.buildUpSizeMin || a.buildUpSizeMax) return true;
    if (a.landSizeMin || a.landSizeMax) return true;
    if (a.minBaths) return true;
    if (a.minParking) return true;
    if (a.is_featured) return true;
    return false;
  }, [searchQuery, advancedFilters]);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setAdvancedFilters(DEFAULT_ADVANCED_FILTERS);
  }, [setSearchQuery, setAdvancedFilters]);

  const { isCreatingShowcase, handleShareAction } = useVitrinaShare(
    () => selectedProperties,
    {
      shareTitle: 'Propiedades',
      onCopied: () => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500); },
    },
  );

  // Quick-filter chips, extracted so the same markup can render inline at xl+
  // and as a dedicated full-width second row below xl (see Filter Bar). The
  // Destacadas chip is hidden when the agency has no featured purchases
  // (matches FeaturedManager's visibility gate).
  const quickFilterChips = QUICK_FILTERS
    .filter(f => f.key !== 'destacadas' || featuredMgmt.hasPurchases)
    .map(f => {
      const isActive = activeQuickFilter === f.key;
      return (
        <button
          key={f.key}
          onClick={() => handleQuickFilter(f.key)}
          className={clsx(
            "px-3 py-1.5 rounded-lg text-xs font-bold transition-all border whitespace-nowrap",
            isActive
              ? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20"
              : isLight
                ? "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                : "bg-transparent border-white/10 text-slate-400 hover:bg-white/5"
          )}
        >
          {f.label}
          {chipCount(f.key) !== null && (
            <span className={clsx(
              "ml-1.5 text-xs font-extrabold tabular-nums",
              isActive
                ? "text-orange-500 dark:text-orange-400"
                : isLight ? "text-slate-400" : "text-slate-500"
            )}>
              {chipCount(f.key)}
            </span>
          )}
        </button>
      );
    });

  // Scope toggle (Mías ↔ Toda la agencia) — only for agents WITH the
  // "Ver todas las propiedades" permission. Agents without it are locked to
  // their own properties and never see this control; admins always see all.
  const scopeToggle = canToggleScope ? (
    <div className={clsx(
      "flex items-center p-1 rounded-xl border shrink-0",
      isLight ? "bg-slate-50 border-slate-200" : "bg-black/20 border-white/5"
    )}>
      {([
        { val: false, label: 'Mías' },
        { val: true, label: 'Toda la agencia' },
      ] as const).map(opt => {
        const isActive = scopeAll === opt.val;
        return (
          <button
            key={opt.label}
            onClick={() => setScopeAll(opt.val)}
            className={clsx(
              "px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap",
              isActive
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <div className="animate-page-entry flex flex-col space-y-6">

      {/* Destaques are managed by the agency admin only (owner decision
          2026-10-30; the featured RPCs enforce it too). Hiding the bar also
          hides "Gestionar", the only way into the card's Destacar/Quitar. */}
      {!isAgentRole && <FeaturedManager
        theme={theme}
        summary={featuredMgmt.summary}
        manageMode={featuredManageMode}
        onToggleManageMode={() => setFeaturedManageMode(v => !v)}
        searchCandidates={featuredMgmt.searchCandidates}
        onActivate={handlePickFeatured}
      />}

      {purchasePrompt && (() => {
        const isSwitch = purchasePrompt.mode === 'switch';
        const current = isSwitch
          ? (featuredMgmt.summary?.featured_properties ?? []).find(p => p.id === purchasePrompt.propertyId)?.purchase_id ?? null
          : null;
        return (
          <FeaturedPurchaseChooser
            theme={theme}
            title={isSwitch ? 'Cambiar tipo de destaque' : '¿Con cuál destaque?'}
            subtitle={isSwitch
              ? 'La propiedad se moverá al paquete que elijas y vencerá con él.'
              : 'Tienes más de un paquete activo. Elige cuál quieres usar.'}
            purchases={isSwitch
              ? featuredMgmt.switchTargetsFor(purchasePrompt.propertyId)
              : featuredMgmt.purchasesWithSlots}
            currentPurchaseId={current}
            busy={featuredMgmt.actioningId === purchasePrompt.propertyId}
            onClose={() => setPurchasePrompt(null)}
            onPick={async (purchaseId) => {
              const { mode, propertyId } = purchasePrompt;
              setPurchasePrompt(null);
              if (mode === 'switch') await featuredMgmt.switchPurchase(propertyId, purchaseId);
              else await finishActivate(propertyId, purchaseId);
            }}
          />
        );
      })()}

      {/* ── Top Header & Actions ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">

        {/* Tabs */}
        <div className="flex space-x-1 p-1 rounded-xl bg-slate-900/5 dark:bg-white/5 border border-slate-200 dark:border-white/5 w-full md:w-auto">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setSelectedProperties([]); }}
              className={clsx(
                'flex-1 md:flex-none px-4 md:px-6 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-semibold transition-all duration-200 capitalize text-center',
                activeTab === tab
                  ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              )}
            >
              {tab === 'expiradas' ? 'Expirando' : tab}
            </button>
          ))}
        </div>

        {/* Global Actions — search + view toggle + Nueva Propiedad now live in
            the app top bar (portaled into #app-header-slot below). Only the
            caps indicator stays inline here. Mobile keeps its FAB. */}
        <div className="hidden md:flex items-center gap-3 self-end md:self-auto">
          <AgencyCapsIndicator theme={theme} show="properties" refreshKey={properties.length} />
        </div>
      </div>

      {/* ── Quick filters + sort — one desktop row. Chips on the left (scope +
          Todas/Publicadas/…); Limpiar + sort on the right. Search, view toggle
          and Nueva Propiedad moved into the app top bar (portaled below), so
          the old full-width toolbar card is gone — reclaiming a row. ── */}
      <div className="hidden md:flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {scopeToggle}
          {scopeToggle && (
            <div className="h-6 w-px bg-slate-200 dark:bg-white/10 mx-1"></div>
          )}
          {quickFilterChips}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className={clsx(
                "flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-all",
                isLight
                  ? "bg-white border-rose-200 text-rose-600 hover:bg-rose-50"
                  : "bg-rose-500/10 border-rose-500/20 text-rose-300 hover:bg-rose-500/20"
              )}
              title="Limpiar búsqueda y filtros"
            >
              <X size={16} />
              <span>Limpiar</span>
            </button>
          )}

          <div className="relative flex items-center">
            <ArrowUpDown
              size={15}
              className="pointer-events-none absolute left-3 text-slate-400"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as PropertySortBy)}
              className={clsx(
                "appearance-none pl-9 pr-8 py-2 rounded-xl text-sm font-medium border transition-all cursor-pointer",
                isLight
                  ? "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                  : "bg-white/5 border-white/10 hover:bg-white/10 text-slate-300"
              )}
              title="Ordenar propiedades"
            >
              <option value="default">Predeterminado</option>
              <option value="created_date">Fecha de creación</option>
              <option value="published_date">Fecha de publicación</option>
              <option value="updated_date">Última modificación</option>
              <option value="price">Precio</option>
              <option value="built_up_area">Área construida</option>
            </select>
            <ChevronDown
              size={15}
              className="pointer-events-none absolute right-2.5 text-slate-400"
            />
          </div>

          <button
            onClick={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}
            title={sortDir === 'desc' ? 'Orden descendente (mayor primero)' : 'Orden ascendente (menor primero)'}
            aria-label="Invertir orden"
            className={clsx(
              "flex items-center justify-center p-2 rounded-xl border transition-all",
              isLight ? "bg-white border-slate-200 hover:bg-slate-50 text-slate-700" : "bg-white/5 border-white/10 hover:bg-white/10 text-slate-300"
            )}
          >
            {sortDir === 'desc' ? <ArrowDownNarrowWide size={16} /> : <ArrowUpNarrowWide size={16} />}
          </button>
        </div>
      </div>

      {/* ── Top-bar action slot (desktop) — search + view toggle + Nueva
          Propiedad, portaled into AppLayout's #app-header-slot so they ride
          in the fixed app bar instead of a page toolbar. Rendered only once
          the slot is resolved (see headerSlot). Escapes the page wrapper's
          animate-page-entry transform for free (the slot lives above it). ── */}
      {headerSlot && createPortal(
        <div className="flex items-center gap-3 w-full">
          <div className={clsx(
            "flex items-center gap-2 px-3 py-2 rounded-xl border flex-1 min-w-0 max-w-2xl",
            isLight ? "bg-slate-900/5 border-slate-200" : "bg-white/5 border-white/10"
          )}>
            {isSearching
              ? <Loader2 size={16} className="text-indigo-500 shrink-0 animate-spin" />
              : <Search size={16} className="text-slate-400 shrink-0" />}
            <input
              type="text"
              placeholder="Buscar por ID, título, zona o propietario..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none focus:ring-0 text-sm w-full placeholder:text-slate-500"
              aria-label="Buscar propiedades"
            />
          </div>

          <CurrencyTogglePill theme={theme} />

          <div className={clsx(
            "flex items-center p-1 rounded-xl border shrink-0",
            isLight ? "bg-slate-50 border-slate-200" : "bg-black/20 border-white/5"
          )}>
            <button
              onClick={() => setViewMode('grid')}
              aria-label="Vista de cuadrícula"
              className={clsx(
                "p-2 rounded-lg transition-all",
                viewMode === 'grid'
                  ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
              )}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              aria-label="Vista de lista"
              className={clsx(
                "p-2 rounded-lg transition-all",
                viewMode === 'list'
                  ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
              )}
            >
              <List size={16} />
            </button>
          </div>

          {/* Filtros — moved out of the old floating button into the top-bar
              slot so it sits with the other page controls. Turns indigo when
              any advanced filter is active; the dot echoes the old affordance. */}
          <button
            onClick={() => setIsFiltersModalOpen(true)}
            aria-label="Filtros avanzados"
            title="Filtros avanzados"
            className={clsx(
              "relative flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold border transition-all shrink-0",
              hasActiveFilters
                ? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-500"
                : isLight
                  ? "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  : "bg-white/5 text-slate-100 border-white/10 hover:bg-white/10"
            )}
          >
            <Filter size={18} />
            <span className="hidden lg:inline">Filtros</span>
            {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-orange-300" />}
          </button>

          <button
            onClick={onAddProperty}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl font-bold shadow-lg shadow-indigo-600/20 transition-all font-outfit shrink-0"
            aria-label="Nueva propiedad"
            title="Nueva propiedad"
          >
            <Plus size={18} />
            <span className="hidden lg:inline whitespace-nowrap">Nueva Propiedad</span>
          </button>
        </div>,
        headerSlot
      )}

      {/* ── Mobile Quick Filter Bar (fixed bottom) — same chips as the
          desktop inline row, but rendered as a sticky bottom bar on
          screens <md. Hidden when the bulk-actions bar is showing so the
          two never overlap. createPortal escapes the page wrapper's
          transform context (same reason as the bulk actions bar below). ── */}
      {selectedProperties.length === 0 && typeof document !== 'undefined' && createPortal(
        <div
          className="md:hidden fixed left-0 right-0 bottom-0 z-40 px-3 pt-2 pb-2 bg-white/95 backdrop-blur border-t border-slate-200"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' } as React.CSSProperties}
        >
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {/* Scope toggle (mobile) — only for agents with the view-all
                permission; mirrors the desktop Mías / Toda la agencia control. */}
            {canToggleScope && (
              <>
                {([
                  { val: false, label: 'Mías' },
                  { val: true, label: 'Toda la agencia' },
                ] as const).map(opt => {
                  const isActive = scopeAll === opt.val;
                  return (
                    <button
                      key={opt.label}
                      onClick={() => setScopeAll(opt.val)}
                      className={clsx(
                        "px-4 py-2 rounded-xl text-xs font-bold transition-all border whitespace-nowrap shrink-0",
                        isActive
                          ? "bg-indigo-500/10 text-indigo-600 border-indigo-500/30"
                          : "bg-white border-slate-200 text-slate-600"
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
                <div className="self-stretch w-px shrink-0 bg-slate-200 mx-1" />
              </>
            )}
            {QUICK_FILTERS
              .filter(f => f.key !== 'destacadas' || featuredMgmt.hasPurchases)
              .map(f => {
                const isActive = activeQuickFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => handleQuickFilter(f.key)}
                    className={clsx(
                      "px-4 py-2 rounded-xl text-xs font-bold transition-all border whitespace-nowrap shrink-0",
                      // Bar surface is always white (see parent), so chips
                      // ignore dark-theme variants here — slate-on-white reads
                      // cleanly in both themes.
                      isActive
                        ? "bg-indigo-500/10 text-indigo-600 border-indigo-500/30"
                        : "bg-white border-slate-200 text-slate-600"
                    )}
                  >
                    {f.label}
                    {chipCount(f.key) !== null && (
                      <span className={clsx(
                        "ml-1.5 text-xs font-extrabold tabular-nums",
                        isActive ? "text-orange-500" : "text-slate-400"
                      )}>
                        {chipCount(f.key)}
                      </span>
                    )}
                  </button>
                );
              })}
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-1 px-4 py-2 rounded-xl text-xs font-bold transition-all border whitespace-nowrap shrink-0 bg-white border-rose-200 text-rose-600"
                aria-label="Limpiar búsqueda y filtros"
              >
                <X size={12} />
                Limpiar
              </button>
            )}

            {/* Sort selector — separated from the filter chips. Two pills
                mirror the desktop toolbar's Predeterminado / Fecha de creación
                dropdown. */}
            <div className="self-stretch w-px shrink-0 bg-slate-200 mx-1" />
            <ArrowUpDown size={14} className="shrink-0 text-slate-400" />
            {([
              { key: 'default', label: 'Predeterminado' },
              { key: 'created_date', label: 'Fecha de creación' },
              { key: 'published_date', label: 'Fecha de publicación' },
              { key: 'updated_date', label: 'Última modificación' },
              { key: 'price', label: 'Precio' },
              { key: 'built_up_area', label: 'Área construida' },
            ] as const).map(opt => {
              const isActive = sortBy === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={clsx(
                    "px-4 py-2 rounded-xl text-xs font-bold transition-all border whitespace-nowrap shrink-0",
                    isActive
                      ? "bg-indigo-500/10 text-indigo-600 border-indigo-500/30"
                      : "bg-white border-slate-200 text-slate-600"
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
            <button
              onClick={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}
              aria-label="Invertir orden"
              className="flex items-center justify-center p-2 rounded-xl border whitespace-nowrap shrink-0 bg-white border-slate-200 text-slate-600"
            >
              {sortDir === 'desc' ? <ArrowDownNarrowWide size={16} /> : <ArrowUpNarrowWide size={16} />}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* ── Bulk Actions Bar — rendered via createPortal into document.body
          so it escapes the page wrapper's transform-containing-block (the
          .animate-page-entry keyframes leave `transform: translateY(0)` on
          the element, which would otherwise reframe `position: fixed` to the
          wrapper's huge scroll height instead of the viewport).
          Mobile: floats at the bottom. Desktop: pinned to the top-right. ── */}
      {selectedProperties.length > 0 && typeof document !== 'undefined' && createPortal(
        <div
          className={clsx(
            "animate-in fade-in slide-in-from-bottom-4 z-[100]",
            "fixed left-3 right-3 bottom-3",
            "sm:left-auto sm:right-6 sm:bottom-auto sm:top-20 sm:w-auto sm:max-w-2xl"
          )}
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' } as React.CSSProperties}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-3 rounded-2xl bg-indigo-600 text-white shadow-xl shadow-indigo-600/20">
            <div className="flex items-center justify-between sm:justify-start gap-4 sm:pl-2">
              <div className="flex items-center gap-2">
                <span className="font-bold">{selectedProperties.length}</span>
                <span className="text-indigo-200 text-sm">seleccionadas</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-white/20"></div>
              <button onClick={() => setSelectedProperties([])} className="text-sm font-medium hover:text-indigo-200 transition-colors">
                Cancelar
              </button>
            </div>
            {/* Single Compartir trigger opens ShareOptionsDialog (contact
                toggle + 4 channels). Mirrors the property detail flow and
                the mobile bulk-select bar. */}
            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              {linkCopied ? (
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/20 text-white text-sm font-bold">
                  <Check size={16} /> Vínculo copiado
                </div>
              ) : (
                <button
                  onClick={() => setShareDialogOpen(true)}
                  disabled={isCreatingShowcase}
                  className="flex items-center justify-center gap-2 px-5 py-2 sm:py-1.5 rounded-xl bg-white text-indigo-700 hover:bg-indigo-50 text-sm font-bold transition-colors disabled:opacity-50"
                >
                  {isCreatingShowcase ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                  Compartir
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Main Content Area ── */}
      <div className="pb-32">

        {/* Cross-search bridge: carry the current filter state into
            /intercambio/propiedades. Hidden until the user has at least one
            transferable filter set — otherwise clicking would do nothing
            useful. Skipped filters: publishStatus, shareStatus, agent_ids,
            minBaths, minParking. */}
        {transferableCount > 0 && (
          <div className={clsx(
            "mb-6 p-4 sm:p-5 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4",
            isLight ? "bg-indigo-50/40 border-indigo-100" : "bg-indigo-500/5 border-indigo-500/20"
          )}>
            <div className="flex items-start gap-3">
              <div className={clsx(
                "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                isLight ? "bg-indigo-100 text-indigo-600" : "bg-indigo-500/15 text-indigo-400"
              )}>
                <Globe size={18} />
              </div>
              <div>
                <p className={clsx("text-sm font-black mb-0.5", isLight ? "text-slate-900" : "text-white")}>
                  ¿Buscas algo similar en la red Intercambio?
                </p>
                <p className={clsx("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>
                  Aplica estos mismos {transferableCount === 1 ? 'filtro' : 'filtros'} a propiedades compartidas por otras agencias.
                </p>
              </div>
            </div>
            <button
              onClick={handleSearchInExchange}
              className="shrink-0 self-stretch sm:self-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold shadow-lg shadow-indigo-600/20 transition-all whitespace-nowrap"
            >
              <Globe size={16} />
              Buscar en Intercambio
              <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-black">{transferableCount}</span>
            </button>
          </div>
        )}

        {/* Results total — reflects the current filter set (server count,
            independent of pagination), so the user sees how many properties
            match after filtering, not just the loaded page. */}
        {propertiesTotalCount !== null && isPropertiesInitialized && !isSearching && filteredProperties.length > 0 && (
          <p className={clsx("mb-4 text-sm font-semibold", isLight ? "text-slate-500" : "text-slate-400")}>
            <span className={clsx("font-black tabular-nums", isLight ? "text-slate-900" : "text-white")}>{propertiesTotalCount.toLocaleString('es-GT')}</span>
            {' '}{propertiesTotalCount === 1 ? 'propiedad' : 'propiedades'}
            {hasActiveFilters && (propertiesTotalCount === 1 ? ' encontrada' : ' encontradas')}
          </p>
        )}

        {filteredProperties.length === 0 ? (
          isSearching ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Loader2 size={48} className="text-indigo-500 animate-spin mb-4" />
              <h3 className={clsx("text-lg font-bold mb-1", isLight ? "text-slate-900" : "text-white")}>Buscando…</h3>
              <p className={clsx("text-sm max-w-md", isLight ? "text-slate-500" : "text-slate-400")}>
                Estamos buscando propiedades que coincidan con "{searchQuery.trim()}".
              </p>
            </div>
          ) : (!isPropertiesInitialized || isLoadingMoreProperties) ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Loader2 size={48} className="text-indigo-500 animate-spin mb-4" />
              <h3 className={clsx("text-lg font-bold mb-1", isLight ? "text-slate-900" : "text-white")}>Cargando propiedades…</h3>
            </div>
          ) : (canToggleScope && !scopeAll) ? (
            // Escape hatch: an agent with "Ver todas" permission whose own-only
            // ("Mías") view is empty would otherwise hit a dead screen if the
            // scope toggle is missed/not visible. Offer the switch here directly.
            // Keyed on canToggleScope (effective profile) so it also shows for a
            // super_admin impersonating this agent.
            <EmptyState
              icon={Building2}
              title="Estás viendo solo tus propiedades"
              description="No hay propiedades propias en esta sección. Cambia a toda la agencia para ver las de tus colegas."
              primaryAction={{ label: 'Ver todas las propiedades de la agencia', icon: Building2, onClick: () => setScopeAll(true) }}
            />
          ) : hasActiveFilters ? (
            <EmptyState
              icon={SearchX}
              title="Ninguna propiedad coincide"
              description={searchQuery.trim()
                ? `No hay resultados para "${searchQuery.trim()}" en esta sección.`
                : 'No hay resultados con los filtros actuales en esta sección.'}
              primaryAction={{ label: 'Limpiar filtros', icon: FilterX, onClick: handleClearFilters }}
            />
          ) : (
            <EmptyState
              icon={Building2}
              title={EMPTY_TAB_COPY[activeTab].title}
              description={EMPTY_TAB_COPY[activeTab].description}
              primaryAction={activeTab === 'activas'
                ? { label: 'Agregar propiedad', icon: Plus, onClick: onAddProperty }
                : { label: 'Ver propiedades activas', onClick: () => setActiveTab('activas') }}
            />
          )
        ) : (
          <>
            <div className={clsx(
              showGrid
                ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6"
                : "flex flex-col gap-4"
            )}>
              {filteredProperties.map((prop, idx) => (
                <div
                  key={prop.id}
                  data-property-id={String(prop.id)}
                  className={clsx(
                    "stagger-item relative group rounded-2xl transition-all duration-500",
                    highlightedId === String(prop.id) && "ring-4 ring-indigo-500 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-950 shadow-2xl shadow-indigo-500/30",
                  )}
                  style={{ '--i': idx } as React.CSSProperties}
                >
                  <div onClick={() => { saveLastVisited(String(prop.id)); onPropertySelect(prop); }} className="cursor-pointer h-full">
                    <PropertyCard
                      property={prop}
                      theme={theme}
                      onStatusChange={onStatusChange}
                      onPublishToggle={onPublishToggle}
                      onShareToggle={onShareToggle}
                      onRestore={onRestore}
                      onPermanentDelete={onPermanentDelete}
                      onActivateDraft={(id) => {
                        const target = properties.find(p => p.id === id);
                        if (target) setActivateTarget(target);
                      }}
                      isListMode={!showGrid}
                      isSelected={selectedProperties.includes(prop.id)}
                      onSelectToggle={(e) => handlePropertySelectToggle(prop.id, e)}
                      matchingCount={matchingCounts[prop.id]}
                      onMatchingClick={(e) => { e.stopPropagation(); setMatchingReqPropertyId(prop.id); }}
                      featuredManageMode={featuredManageMode}
                      featuredSlotsAvailable={slotsAvailable}
                      onToggleFeatured={handleToggleFeatured}
                      onSwitchFeatured={handleSwitchFeatured}
                      featuredCanSwitch={(featuredMgmt.summary?.purchases?.length ?? 0) > 1}
                      featuredActioning={featuredMgmt.actioningId === String(prop.id)}
                    />
                  </div>
                </div>
              ))}
            </div>

            {hasMore && onLoadMore && (
              <div className="flex justify-center mt-12 mb-6">
                <button
                  onClick={onLoadMore}
                  disabled={isLoadingMore}
                  className={clsx(
                    "px-8 py-3.5 rounded-2xl font-bold transition-all shadow-lg flex items-center min-w-[200px] justify-center gap-3",
                    isLight
                      ? "bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/20"
                      : "bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/20 shadow-indigo-500/5",
                    isLoadingMore && "opacity-70 cursor-not-allowed"
                  )}
                >
                  {isLoadingMore ? (
                    <><div className={clsx("w-5 h-5 border-2 border-t-transparent rounded-full animate-spin", isLight ? "border-white" : "border-indigo-400")}></div> Cargando...</>
                  ) : <span className="font-black tracking-wide">Cargar Más Propiedades</span>}
                </button>
              </div>
            )}

          </>
        )}
      </div>

      <PropertyFiltersModal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        theme={theme}
        currentFilters={advancedFilters}
        onApplyFilters={setAdvancedFilters}
        showAgentFilter={!restrictToOwn}
      />
      {/* ── Mobile Action Center (FAB & Overlays) - Portaled to Body ── */}
      {typeof document !== 'undefined' && createPortal(
        <div className="md:hidden">
          {/* Search Overlay */}
          {isSearchVisible && (
            <div className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-md p-6 flex flex-col animate-in fade-in duration-300">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-black text-white">Buscar Propiedad</h2>
                <button onClick={() => setIsSearchVisible(false)} className="p-2 text-white/50 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              <div className="relative">
                {isSearching
                  ? <Loader2 className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400 animate-spin" size={20} />
                  : <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />}
                <input
                  autoFocus
                  type="text"
                  placeholder="ID, título, zona, propietario..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/10 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white focus:bg-white/20 outline-none transition-all"
                />
              </div>
              <button
                onClick={() => setIsSearchVisible(false)}
                className="mt-6 w-full py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-indigo-600/20"
              >
                Ver Resultados
              </button>
            </div>
          )}

          {/* Hide the FAB + speed-dial entirely while properties are selected so
              the bulk-actions bar at the bottom isn't visually competing with the
              "+" button. The bar takes priority — once selection is cleared, the
              FAB returns. Search/filter remain reachable via the toolbar above. */}
          {selectedProperties.length === 0 && (<>
          {/* Speed Dial Backdrop */}
          {isFabExpanded && (
            <div
              className="fixed inset-0 z-[90] bg-slate-950/40 backdrop-blur-[2px] animate-in fade-in duration-300"
              onClick={() => setIsFabExpanded(false)}
            />
          )}

          {/* Speed Dial Options. Bumped along with the FAB so the children
              still stack above the FAB (FAB at safe-area+3.5rem, speed-dial
              ~4.5rem above that = safe-area+8rem). */}
          <div
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 8rem)' } as React.CSSProperties}
            className={clsx(
              "fixed right-6 z-[95] flex flex-col items-end gap-3 transition-all duration-300",
              isFabExpanded ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0 pointer-events-none"
            )}
          >
            {/* New Property */}
            <div className="flex items-center gap-3">
              <span className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg">Nueva Propiedad</span>
              <button
                onClick={() => { onAddProperty(); setIsFabExpanded(false); }}
                className="w-12 h-12 bg-emerald-500 text-white rounded-xl shadow-lg flex items-center justify-center active:scale-95"
              >
                <Plus size={20} />
              </button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <span className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg">Filtros</span>
              <button
                onClick={() => { setIsFiltersModalOpen(true); setIsFabExpanded(false); }}
                className="w-12 h-12 bg-indigo-600 text-white rounded-xl shadow-lg flex items-center justify-center active:scale-95"
              >
                <Filter size={20} />
              </button>
            </div>

            {/* Search */}
            <div className="flex items-center gap-3">
              <span className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg">Buscar</span>
              <button
                onClick={() => { setIsSearchVisible(true); setIsFabExpanded(false); }}
                className="w-12 h-12 bg-slate-700 text-white rounded-xl shadow-lg flex items-center justify-center active:scale-95"
              >
                <Search size={20} />
              </button>
            </div>


          </div>

          {/* Main FAB. Bottom is computed so the FAB sits ABOVE the new
              quick-filter bar (~46px tall + safe-area). Without the bump
              the FAB would straddle the bar's top edge. */}
          <button
            onClick={() => setIsFabExpanded(!isFabExpanded)}
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 3.5rem)' } as React.CSSProperties}
            className={clsx(
              "fixed right-6 z-[96] w-14 h-14 rounded-2xl shadow-[0_10px_30px_rgba(79,70,229,0.4)] flex items-center justify-center transition-all duration-300 active:scale-90",
              isFabExpanded ? "bg-slate-800 text-white rotate-45" : "bg-indigo-600 text-white"
            )}
          >
            <Plus size={28} />
          </button>
          </>)}
        </div>,
        document.body
      )}

      {/* Matching Requirements Modal */}
      {matchingReqPropertyId && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={closeMatchingModal} />
          <div className={clsx(
            "relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-3xl shadow-2xl overflow-hidden border",
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-white/10"
          )}>
            <div className={clsx(
              "flex-shrink-0 flex items-center justify-between p-6 border-b",
              isLight ? "border-slate-100 bg-slate-50/50" : "border-white/5 bg-white/5"
            )}>
              <div>
                <h2 className={clsx("text-xl font-black mb-1", isLight ? "text-slate-900" : "text-white")}>
                  Requerimientos que Coinciden
                </h2>
                <p className={clsx("text-sm", isLight ? "text-slate-500" : "text-slate-400")}>
                  Colegas que buscan propiedades como la tuya
                </p>
              </div>
              <button onClick={closeMatchingModal}
                className={clsx("p-2.5 rounded-xl transition-all", isLight ? "bg-white border border-slate-200 text-slate-500 hover:text-slate-900" : "bg-white/5 border border-white/5 text-slate-400 hover:text-white")}>
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
              {isLoadingMatches ? (
                <div className="flex flex-col items-center justify-center h-48">
                  <Loader2 size={36} className="text-indigo-500 animate-spin mb-4" />
                  <p className={clsx("text-sm", isLight ? "text-slate-500" : "text-slate-400")}>Buscando coincidencias en la red...</p>
                </div>
              ) : matchingRequirements.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-center">
                  <ClipboardList size={40} className="text-slate-400 mb-4" />
                  <h3 className={clsx("text-lg font-bold mb-1", isLight ? "text-slate-900" : "text-white")}>Sin coincidencias</h3>
                  <p className={clsx("text-sm max-w-sm", isLight ? "text-slate-500" : "text-slate-400")}>
                    No hay requerimientos compartidos de otros colegas que coincidan con esta propiedad.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {matchingRequirements.map((req: any) => (
                    <div key={req.requirement_id} className={clsx(
                      "p-5 rounded-2xl border transition-all",
                      isLight ? "bg-white border-slate-100 hover:border-indigo-200 hover:shadow-md" : "bg-slate-900 border-white/5 hover:border-indigo-500/30"
                    )}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className={clsx("text-sm font-black", isLight ? "text-slate-900" : "text-white")}>
                            {stripHtml(req.requirement_name) || 'Requerimiento'}
                          </p>
                        </div>
                        <span className={clsx("px-2 py-0.5 rounded-chip text-[9px] font-bold border",
                          req.operation === 'sale' ? "bg-amber-500/15 text-amber-500 border-amber-500/20" : "bg-indigo-500/15 text-indigo-500 border-indigo-500/20"
                        )}>
                          {req.operation === 'sale' ? 'Venta' : 'Alquiler'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 mb-3 text-xs text-slate-500">
                        {(req.min_price || req.max_price) && (
                          <span className="font-semibold flex items-center gap-1">
                            <span className="text-emerald-500">$</span>
                            {(req.min_price || 0).toLocaleString('en-US')} — {(req.max_price || 0).toLocaleString('en-US')}
                          </span>
                        )}
                        {(req.bedrooms_min || req.bedrooms_max) && (
                          <span className="flex items-center gap-1">🛏 {req.bedrooms_min || '—'}–{req.bedrooms_max || '—'} hab.</span>
                        )}
                        {req.property_type_names && <span>🏠 {req.property_type_names}</span>}
                      </div>
                      <div className={clsx("flex items-center gap-2 pt-3 border-t", isLight ? "border-slate-100" : "border-white/5")}>
                        <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-[9px] font-bold text-white">
                          {(req.agent_name || 'A')[0]}
                        </div>
                        <div>
                          <p className={clsx("text-[10px] font-bold", isLight ? "text-slate-700" : "text-slate-300")}>{req.agent_name || 'Agente'}</p>
                          <p className="text-[9px] text-slate-400">{req.agency_name || ''}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <ActivateDraftModal
        isOpen={!!activateTarget}
        property={activateTarget}
        onClose={() => setActivateTarget(null)}
        onActivated={(id, patch) => {
          // Optimistic local update — the card immediately drops out of
          // Borradores into Activas without waiting for a full refetch. The DB
          // triggers (auto_publish_expiry + share-expiry sibling) will populate
          // the *_at / *_expires_at columns; the list refreshes naturally on
          // the next fetch.
          setProperties(prev => prev.map(p => p.id === id ? {
            ...p,
            crm_status: 'active',
            publish_status: patch.publish ? 'published' : 'unpublished',
            share_status: patch.share ? 'shared' : 'not_shared',
          } as Property : p));
        }}
      />

      <ShareOptionsDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onSelect={handleShareAction}
        title={selectedProperties.length > 1 ? `Compartir ${selectedProperties.length} propiedades` : 'Compartir propiedad'}
      />
    </div>
  );
};
